// Copyright © 2026 Alex Bruce. All rights reserved.
//
// Local rolling backups, partitioned by project.
//
// These used to be one flat array shared by every project. The 5-minute
// autosave wrote the whole of whatever estimate was open -- every line item,
// every cost, the client's name -- and the recovery check on mount read
// `backups[0]`, the newest entry from whichever project was last open, with no
// project identity on it at all. Open a large estimate, then open a different
// one, and the app offered to "recover unsaved work" that belonged to another
// client. Accepting it loaded their estimate into the project in front of you.
//
// Everything here is keyed by project id, and a backup is only ever offered
// back to the project that produced it.

export interface BackupEntry {
  ts: number;
  /** The project this state came from. Entries without one are not trusted. */
  pid: string;
  projectName?: string;
  state: any;
}

export type BackupStore = Record<string, BackupEntry[]>;

/** Backups kept per project, and projects kept at all. */
export const MAX_PER_PROJECT = 3;
export const MAX_PROJECTS = 6;

/**
 * Normalise whatever is in storage into a store.
 *
 * A legacy flat array is DISCARDED rather than migrated. Its entries carry no
 * project id, so there is no honest way to attribute them -- and guessing
 * wrong is precisely the failure this partitioning exists to prevent. At most
 * five minutes of work is lost; the alternative risks handing one client's
 * estimate to another.
 */
export function readBackups(raw: unknown): BackupStore {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: BackupStore = {};
  for (const [pid, list] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(list)) continue;
    const clean = list.filter(
      (e: any): e is BackupEntry =>
        !!e && typeof e === "object" && typeof e.ts === "number" && e.pid === pid
    );
    if (clean.length) out[pid] = clean;
  }
  return out;
}

/**
 * Add an entry for one project, trimming that project's history and then the
 * number of projects retained -- oldest touched first, so a machine that has
 * seen fifty projects does not keep fifty estimates in the browser.
 */
export function addBackup(
  store: BackupStore,
  entry: BackupEntry,
  maxPerProject = MAX_PER_PROJECT,
  maxProjects = MAX_PROJECTS,
): BackupStore {
  const mine = [entry, ...(store[entry.pid] ?? [])].slice(0, maxPerProject);
  const next: BackupStore = { ...store, [entry.pid]: mine };
  return Object.fromEntries(
    Object.entries(next)
      .sort((a, b) => (b[1][0]?.ts ?? 0) - (a[1][0]?.ts ?? 0))
      .slice(0, maxProjects)
  );
}

/** The newest backup for one project, or null. Never another project's. */
export function newestFor(store: BackupStore, pid: string): BackupEntry | null {
  const list = store[pid];
  if (!Array.isArray(list) || list.length === 0) return null;
  return list.reduce((a, b) => (b.ts > a.ts ? b : a));
}

/**
 * Whether a backup is worth offering back.
 *
 * The comparison used to be against the LOCAL project store even when a cloud
 * project was open, so it measured an unrelated estimate. It is against the
 * live item count of the project actually on screen, and the margin keeps a
 * couple of rows deleted on purpose from reading as lost work.
 */
export function shouldOfferRecovery(
  entry: BackupEntry | null,
  liveItemCount: number,
  margin = 5,
): boolean {
  if (!entry) return false;
  const backupItems = Object.keys(entry.state?.itemState ?? {}).length;
  return backupItems > liveItemCount + margin;
}
